import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { apiRequest } from '../../utils/apiClient';
import { useDeveloper } from '../../contexts/DeveloperContext';
import { hasPermission } from '../../utils/permissions';

interface Member {
  id: string;
  role: string;
  status: string;
  acceptedAt?: string | null;
  user: { id: string; firstName?: string | null; lastName?: string | null; email: string; avatarUrl?: string | null };
}

export default function DeveloperTeamPage() {
  const { i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const t = (en: string, ar: string) => (isRTL ? ar : en);
  const { myRole } = useDeveloper();
  const canInvite = hasPermission(myRole, 'ADMIN');

  const [members, setMembers] = useState<Member[]>([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'ADMIN' | 'AGENT' | 'VIEWER'>('AGENT');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    apiRequest<{ data: Member[] }>('/developers/my/team').then((res) => setMembers(res.data ?? [])).catch(() => setMembers([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const invite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    setMsg('');
    try {
      await apiRequest('/developers/my/team/invite', { method: 'POST', body: { email: email.trim(), role } });
      setMsg(t('Invitation sent.', 'تم إرسال الدعوة.'));
      setEmail('');
      load();
    } catch (err) {
      setMsg(err instanceof Error ? err.message : t('Failed to invite.', 'تعذّر الإرسال.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" dir={i18n.dir()}>
      <Navigation />
      <main className="mx-auto max-w-3xl px-4 py-8">
        <h1 className={`mb-6 text-xl font-bold text-[var(--pv-color-primary)] ${isRTL ? 'text-right' : ''}`}>{t('Team', 'الفريق')}</h1>

        {canInvite && (
          <form onSubmit={invite} className={`mb-6 rounded-2xl border border-slate-200/80 bg-white p-5 ${isRTL ? 'text-right' : ''}`}>
            <h2 className="mb-3 text-sm font-semibold text-slate-700">{t('Invite a member', 'دعوة عضو')}</h2>
            <div className="flex flex-col gap-3 sm:flex-row">
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('Email', 'البريد الإلكتروني')} dir="ltr"
                className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-[#004D40]" />
              <select value={role} onChange={(e) => setRole(e.target.value as typeof role)} className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm">
                <option value="ADMIN">{t('Admin', 'مدير')}</option>
                <option value="AGENT">{t('Agent', 'وكيل')}</option>
                <option value="VIEWER">{t('Viewer', 'مشاهد')}</option>
              </select>
              <button type="submit" disabled={busy} className="rounded-xl bg-[#004D40] px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
                {busy ? t('Sending…', 'جارٍ الإرسال…') : t('Invite', 'دعوة')}
              </button>
            </div>
            {msg && <p className="mt-2 text-sm text-slate-500">{msg}</p>}
          </form>
        )}

        <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white">
          {members.length === 0 ? (
            <p className="p-6 text-center text-sm text-slate-500">{t('No team members yet.', 'لا يوجد أعضاء بعد.')}</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {members.map((m) => {
                const fullName = [m.user.firstName, m.user.lastName].filter(Boolean).join(' ') || m.user.email;
                return (
                  <li key={m.id} className={`flex items-center justify-between gap-3 p-4 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                    <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      {m.user.avatarUrl ? (
                        <img src={m.user.avatarUrl} alt={fullName} className="h-9 w-9 rounded-full object-cover" />
                      ) : (
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--pv-color-surface-sunken)] text-sm font-bold text-[#003930]">{fullName.charAt(0).toUpperCase()}</div>
                      )}
                      <div>
                        <div className="text-sm font-medium text-slate-800">{fullName}</div>
                        <div className="text-xs text-slate-400" dir="ltr">{m.user.email}</div>
                      </div>
                    </div>
                    <span className="rounded-full bg-[var(--pv-color-surface-sunken)] px-2.5 py-0.5 text-[11px] font-medium text-[#003930]">{m.role}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
